import React from "react";
import {
  Container,
  Typography,
  Box,
  Link,
  Breadcrumbs,
  Divider,
} from "@mui/material";

const PrivacyPolicy: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 4 }}>
        <Link underline="hover" color="inherit" href="/">
          Home
        </Link>
        <Typography color="text.primary">Privacy Policy</Typography>
      </Breadcrumbs>

      <Typography variant="h3" component="h1" gutterBottom>
        Privacy Policy
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" paragraph>
        Last updated:{" "}
        {new Date().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })}
      </Typography>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Introduction
        </Typography>
        <Typography paragraph>
          At Renoten, we respect your privacy and are committed to protecting
          your personal data. This privacy policy will inform you about how we
          look after your personal data when you visit our website and tell you
          about your privacy rights and how the law protects you.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          The Data We Collect
        </Typography>
        <Typography paragraph>
          When you visit our website, we may collect certain information
          automatically through cookies and similar technologies, including:
        </Typography>
        <ul>
          <Typography component="li" paragraph>
            Usage data (pages visited, time spent on pages, navigation paths)
          </Typography>
          <Typography component="li" paragraph>
            Device information (browser type, operating system)
          </Typography>
          <Typography component="li" paragraph>
            IP address and approximate location (country or city level)
          </Typography>
        </ul>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          How We Use Your Data
        </Typography>
        <Typography paragraph>
          We use the collected data for various purposes:
        </Typography>
        <ul>
          <Typography component="li" paragraph>
            To analyze website usage and improve our services
          </Typography>
          <Typography component="li" paragraph>
            To understand how visitors interact with our website
          </Typography>
          <Typography component="li" paragraph>
            To detect and prevent technical issues
          </Typography>
        </ul>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Google Analytics
        </Typography>
        <Typography paragraph>
          We use Google Analytics to analyze the use of our website. Google
          Analytics gathers information about website use by means of cookies.
          The information gathered is used to create reports about the use of
          our website.
        </Typography>
        <Typography paragraph>
          Google's privacy policy is available at:{" "}
          <Link
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener"
          >
            https://policies.google.com/privacy
          </Link>
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Your Choices
        </Typography>
        <Typography paragraph>
          You can choose to accept or decline cookies. Most web browsers
          automatically accept cookies, but you can usually modify your browser
          settings to decline cookies if you prefer. You can also use our cookie
          consent banner to manage your preferences.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Data Retention
        </Typography>
        <Typography paragraph>
          We will only retain your personal data for as long as necessary to
          fulfill the purposes we collected it for, including for the purposes
          of satisfying any legal, accounting, or reporting requirements.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Your Rights
        </Typography>
        <Typography paragraph>
          Depending on your location, you may have certain rights regarding your
          personal data, including:
        </Typography>
        <ul>
          <Typography component="li" paragraph>
            The right to access your personal data
          </Typography>
          <Typography component="li" paragraph>
            The right to rectification of your personal data
          </Typography>
          <Typography component="li" paragraph>
            The right to erasure of your personal data
          </Typography>
          <Typography component="li" paragraph>
            The right to restrict processing of your personal data
          </Typography>
          <Typography component="li" paragraph>
            The right to data portability
          </Typography>
          <Typography component="li" paragraph>
            The right to object to processing of your personal data
          </Typography>
        </ul>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Contact Us
        </Typography>
        <Typography paragraph>
          If you have any questions about this privacy policy or our privacy
          practices, please contact us at:
        </Typography>
        <Typography paragraph>
          Email:{" "}
          <Link href="mailto:support@renoten.com">support@renoten.com</Link>
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Typography variant="body2" color="text.secondary" align="center">
        © {new Date().getFullYear()} Renoten. All rights reserved.
      </Typography>
    </Container>
  );
};

export default PrivacyPolicy;
